import { useEffect, useMemo, useRef, useState } from "react";

// API
// Interfaces
import { IPeriodTreeLeaf } from "@Layouts/components/SideBars/PayPeriodSideBar/interfaces";
import { Nullable } from "@Common/interfaces/types.ts";

// Context
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";

// Hooks
import useCacheCleaner from "@/Common/hooks/CacheCleaner";

// Subscriptions
import { onUpdateRepairOrders } from "@DataPages/api/repairOrders/subscriptions.ts";

// Components
import { ServiceOperationsModel } from "./ServiceOperationsModel";
import BodyLayer from "@Layouts/components/Layers/BodyLayer";
import CollapsibleLeftPanel from "@/Layouts/components/CollapsibleLeftPanel";
import PayPeriodSideBar from "@Layouts/components/SideBars/PayPeriodSideBar";

// Constants
const QUERY_IDENTIFIERS = ["repairOrders", "advisorPerformance", "technicianFlaggedTime"];

export default function ServiceOperationsLayer() {
	const { clearCache } = useCacheCleaner();
	const lastSelected = useRef<Nullable<IPeriodTreeLeaf>>(null);
	const [selection, setSelection] = useState<Nullable<IPeriodTreeLeaf>>(null);
	const { addSubscription } = useSubscriptions();

	const sideBar = useMemo(() => {
		const sideSelection = function (selected?: IPeriodTreeLeaf | null, isActiveCycle = false) {
			const payPeriodId = selected?.payPeriodId;
			const commissionCycleId = selected?.cycleId;

			if (selected?._id !== lastSelected?.current?._id) {
				lastSelected.current = selected;
				setSelection(selected);

				if (isActiveCycle) {
					addSubscription(["repairOrders", commissionCycleId, payPeriodId], {
						context: "onUpdateRepairOrders",
						query: onUpdateRepairOrders,
						variables: {
							commissionCycleId,
							payPeriodId,
						},
					});
				}
			}
		};

		return <PayPeriodSideBar callbackSelected={sideSelection} selectDefaultLastCommissionCycle />;
	}, [addSubscription]);

	useEffect(() => {
		return () => {
			setSelection(null);
			lastSelected.current = null;
			clearCache(QUERY_IDENTIFIERS);
		};
	}, []);

	return (
		<BodyLayer floatingContent={<CollapsibleLeftPanel control={sideBar} />}>
			<ServiceOperationsModel periodTreeSelection={selection} />
		</BodyLayer>
	);
}
